import Box from "@mui/material/Box";
import { useState } from "react";
import { useDialog } from "../../hooks/useDialog";
import { grants } from "../../backoffice.access_control";
import { drivingSchoolsProvider } from "../../providers/drivingSchoolsProvider";
import { getLessonPriceDifference, type LessonPriceDifference } from "./getLessonPriceDifference";
import { LessonPriceDifferenceDialog } from "./LessonPriceDifferenceDialog";
import { Button, TextInput, useGetList, useGetOne } from "react-admin";
import { DateTime } from "luxon";
import { FormProvider, useForm } from "react-hook-form";
import { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import { Row } from "../../misc/Row";
import { InstructorInput } from "../../inputs/InstructorInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { DrivingLessonDurationInput } from "../../inputs/DrivingLessonDurationInput";
import { DrivingLessonTypeInput } from "../../inputs/DrivingLessonTypeInput";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { VehiclesInput } from "../../inputs/VehiclesInput";
import { OWN_MOTORCYCLE_PSEUDO_ID } from "../../model/VehicleType";
import {
  DrivingLicenseClass,
  isForBus,
  isForBusWithTrailer,
  isForCar,
  isForCarWithTrailer,
  isForMotorcycle,
  isForTractor,
  isForTruck,
  isForTruckWithTrailer,
} from "../../model/DrivingLicenseClass";
import { Student } from "../../providers/studentsProvider";
import { Column } from "../../misc/Column";
import { editDrivingLesson } from "../../api/backoffice.api";
import { Form2Theme } from "../../misc/Form2";
import { StartingPointInput } from "../../inputs/StartingPointInput";
import { StartingPoint, startingPointsProvider } from "../../providers/startingPointsProvider";
import { matchAddresses } from "../../backoffice.utils";
import { useNightLessonInDaylightWarning } from "../../hooks/useNightLessonInDaylightWarning";
import { DRIVING_LESSON_DURATION_OPTIONS } from "../AddAppointmentDialog/CreateDrivingLessonForm";
import { PRACTICAL_EXAM_DURATION_OPTIONS } from "../AddAppointmentDialog/CreateExamForm";
import { SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS } from "../AddAppointmentDialog/CreateSimulatorDrivingLessonForm";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { BufferTimesInput } from "../../inputs/BufferTimesInput";
import { BufferTimes, BufferTimesSchema } from "../../model/BufferTimes";
import safeJsonStringify from "safe-json-stringify";

interface EditDrivingLessonFormProps {
  event: AutovioCalendarEvent;
  closeEditMode: () => void;
}

type DrivingLessonType =
  | "normal"
  | "autobahnfahrt"
  | "nachtfahrt"
  | "ueberlandfahrt"
  | "schaltkompetenz"
  | "simulator"
  | "praktischePruefung"
  | "unterweisungAmFahrzeug"
  | "praktischeUnterweisung";

export interface EditDrivingLessonFormValues {
  instructorId: string;
  studentId: string;
  dateTime: DateTime;
  bufferTimes: BufferTimes;
  drivingLessonType: DrivingLessonType;
  durationInMinutes: number;
  drivingLicenseClass: "" | DrivingLicenseClass;
  carId: string | null;
  motorcycleId: string | null;
  tractorId: string | null;
  secondMotorcycleOrCarId: string | null;
  trailerId: string | null;
  truckId: string | null;
  busId: string | null;
  startingPointId: string;
  remarks: string;
}

interface EditDrivingLessonParams {
  drivingLessonType: DrivingLessonType;
  instructorId: string;
  bookedTrainingId: string;
  dateTime: DateTime;
  duration: { minutes: number };
  bufferTimes: BufferTimes;
  vehicleId: string | null;
  vehicle2Id: string | null;
  startingPointId: string;
  remarks?: string;
}

type VehicleIdKey =
  | "carId"
  | "motorcycleId"
  | "tractorId"
  | "secondMotorcycleOrCarId"
  | "trailerId"
  | "truckId"
  | "busId";

/**
 * Maps a driving license class to the form-value keys that hold the primary and (optional) secondary
 * vehicle for that class. Used to keep {@link validate} and getDefaultVehicles in sync.
 */
function vehicleSlotsForDrivingLicenseClass(
  drivingLicenseClass: DrivingLicenseClass,
  drivingLessonType?: DrivingLessonType,
): {
  vehicle1: VehicleIdKey;
  vehicle2: VehicleIdKey | null;
} {
  if (isForCar(drivingLicenseClass)) {
    return { vehicle1: "carId", vehicle2: isForCarWithTrailer(drivingLicenseClass) ? "trailerId" : null };
  }
  if (isForMotorcycle(drivingLicenseClass)) {
    return {
      vehicle1: "motorcycleId",
      vehicle2: drivingLessonType === "praktischePruefung" ? "carId" : "secondMotorcycleOrCarId",
    };
  }
  if (isForTruck(drivingLicenseClass)) {
    return { vehicle1: "truckId", vehicle2: isForTruckWithTrailer(drivingLicenseClass) ? "trailerId" : null };
  }
  if (isForBus(drivingLicenseClass)) {
    return { vehicle1: "busId", vehicle2: isForBusWithTrailer(drivingLicenseClass) ? "trailerId" : null };
  }
  if (isForTractor(drivingLicenseClass)) {
    return { vehicle1: "tractorId", vehicle2: null };
  }
  throw new Error(`Unsupported driving license class: ${drivingLicenseClass}`);
}

// Used for practical-exams only due to having `resources` field without the dedicated vehicle fields.
function deriveVehiclesFromResources(
  resources: string[] | undefined,
  drivingLicenseClass: DrivingLicenseClass,
): [string | null, string | null] {
  if (!resources || resources.length === 0) {
    return [null, null];
  }
  if (isForMotorcycle(drivingLicenseClass) && resources.length === 1) {
    return [OWN_MOTORCYCLE_PSEUDO_ID, resources[0]];
  }
  return [resources[0], resources[1] ?? null];
}

function validate(formValues: EditDrivingLessonFormValues & { student: Student }): EditDrivingLessonParams {
  const {
    instructorId,
    student,
    dateTime,
    durationInMinutes,
    bufferTimes,
    drivingLessonType,
    drivingLicenseClass,
    carId,
    motorcycleId,
    tractorId,
    secondMotorcycleOrCarId,
    trailerId,
    truckId,
    busId,
    startingPointId,
    remarks,
  } = formValues;
  if (!dateTime) {
    throw new Error(`Invalid dateTime: ${dateTime}`);
  }
  if (!drivingLessonType) {
    throw new Error(`Invalid drivingLessonType: ${drivingLessonType}`);
  }
  if (!student) {
    throw new Error(`Invalid student: ${student}`);
  }
  if (!instructorId) {
    throw new Error(`Invalid instructorId: ${instructorId}`);
  }
  if (!drivingLicenseClass) {
    throw new Error(`Invalid drivingLicenseClass: ${drivingLicenseClass}`);
  }
  const slots = vehicleSlotsForDrivingLicenseClass(drivingLicenseClass, drivingLessonType);
  const vehicleIds: Record<VehicleIdKey, string | null> = {
    carId,
    motorcycleId,
    tractorId,
    secondMotorcycleOrCarId,
    trailerId,
    truckId,
    busId,
  };
  const vehicleId = vehicleIds[slots.vehicle1];
  const vehicle2Id = slots.vehicle2 ? vehicleIds[slots.vehicle2] : null;
  const bookedTraining = student.bookedTrainings.find((it) => it.drivingLicenseClass === drivingLicenseClass);
  if (!bookedTraining) {
    throw new Error(`Student ${student.id} has no booked training for driving license class ${drivingLicenseClass}`);
  }
  if (!durationInMinutes) {
    throw new Error(`Invalid durationInMinutes: ${durationInMinutes}`);
  }
  if (!bufferTimes || !BufferTimesSchema.safeParse(bufferTimes).success) {
    throw new Error(`Invalid bufferTimes: ${safeJsonStringify(bufferTimes)}`);
  }
  if (!startingPointId) {
    throw new Error(`Invalid startingPointId: ${startingPointId}`);
  }
  return {
    drivingLessonType,
    instructorId,
    bookedTrainingId: bookedTraining.id,
    dateTime,
    duration: { minutes: durationInMinutes },
    bufferTimes,
    vehicleId,
    vehicle2Id,
    startingPointId,
    ...(remarks !== null ? { remarks } : {}),
  } satisfies EditDrivingLessonParams;
}

export function EditDrivingLessonForm({ event, closeEditMode }: EditDrivingLessonFormProps) {
  const {
    student: { uid: studentId, bookedTrainingId },
    vehicleId,
    vehicle2Id,
    studentUsesOwnMotorcycle,
  } = event;
  const { data: student } = useGetOne<Student>("students", { id: studentId }, { enabled: !!studentId });
  const bookedTraining = student?.bookedTrainingsById[bookedTrainingId];
  const fallbackStartingPoint = {
    id: "fallbackStartingPoint",
    name: "???",
    postalAddress: {
      street: event.startLocation.street,
      postalCode: event.startLocation.postalCode,
      city: event.startLocation.city,
    },
  };

  const getDefaultVehicles: () => Pick<
    EditDrivingLessonFormValues,
    "carId" | "motorcycleId" | "truckId" | "secondMotorcycleOrCarId" | "trailerId" | "busId" | "tractorId"
  > = () => {
    const none = {
      carId: null,
      motorcycleId: null,
      truckId: null,
      tractorId: null,
      secondMotorcycleOrCarId: null,
      trailerId: null,
      busId: null,
    };
    if (!bookedTraining) {
      return none;
    }
    const slots = vehicleSlotsForDrivingLicenseClass(bookedTraining.drivingLicenseClass, event.drivingLessonType);
    const [effectiveVehicleId, effectiveVehicle2Id] =
      event.drivingLessonType === "praktischePruefung"
        ? deriveVehiclesFromResources(event.resources, bookedTraining.drivingLicenseClass)
        : [vehicleId, vehicle2Id];

    const primaryVehicleId =
      event.drivingLessonType !== "praktischePruefung" &&
      isForMotorcycle(bookedTraining.drivingLicenseClass) &&
      studentUsesOwnMotorcycle
        ? OWN_MOTORCYCLE_PSEUDO_ID
        : effectiveVehicleId;

    return {
      ...none,
      [slots.vehicle1]: primaryVehicleId,
      ...(slots.vehicle2 ? { [slots.vehicle2]: effectiveVehicle2Id } : {}),
    };
  };

  const { data: startingPoints } = useGetList<StartingPoint>(
    "startingPoints",
    {
      sort: { field: "__apply_special_starting_point_sort_logic__", order: "ASC" },
      pagination: { page: 1, perPage: 999 },
      meta: { drivingLessonType: event.drivingLessonType, instructorId: event.instructorId, student },
    },
    {
      enabled: !!(event.drivingLessonType !== "simulator" && student),
    },
  );

  if (!student || (event.drivingLessonType !== "simulator" && !startingPoints)) {
    return null;
  }

  const defaultValues = {
    studentId,
    instructorId: event.instructorId ?? "",
    dateTime: event.start,
    bufferTimes: event.bufferTimes ?? { preparationTime: 0, wrapUpTime: 0 },
    drivingLessonType: event.drivingLessonType,
    durationInMinutes: Math.round(event.end.diff(event.start).as("minutes")),
    drivingLicenseClass: bookedTraining?.drivingLicenseClass ?? "",
    ...getDefaultVehicles(),
    startingPointId:
      startingPoints?.find((it) => matchAddresses(it.postalAddress, event.startLocation))?.id ??
      fallbackStartingPoint.id,
    remarks: event.remarks ?? "",
  } satisfies EditDrivingLessonFormValues;

  return (
    <_EditDrivingLessonForm
      event={event}
      student={student}
      defaultValues={defaultValues}
      fallbackStartingPoint={fallbackStartingPoint}
      closeEditMode={closeEditMode}
    />
  );
}

function _EditDrivingLessonForm(props: {
  event: AutovioCalendarEvent;
  student: Student;
  defaultValues: EditDrivingLessonFormValues;
  fallbackStartingPoint: StartingPoint;
  closeEditMode: () => void;
}) {
  const { event, student, defaultValues, fallbackStartingPoint, closeEditMode } = props;
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [pendingPriceChange, setPendingPriceChange] = useState<{
    edit: Parameters<typeof editDrivingLesson>[0];
    prices: LessonPriceDifference;
  }>();
  const saveChanges = (edit: Parameters<typeof editDrivingLesson>[0]) =>
    runSafely(() => editDrivingLesson(edit), {
      onSuccess: () => {
        closeDialog();
        closeEditMode();
      },
    });
  const formProps = useForm<EditDrivingLessonFormValues>({ defaultValues });
  const formValues = formProps.watch();
  const { instructorId, drivingLessonType, drivingLicenseClass, startingPointId } = formValues;
  const nightLessonInDaylightWarning = useNightLessonInDaylightWarning(formValues);

  const onSubmit = async (formValues: EditDrivingLessonFormValues) => {
    const pending = await runSafely(
      async () => {
        const {
          instructorId,
          dateTime,
          duration,
          bufferTimes,
          drivingLessonType,
          bookedTrainingId,
          vehicleId,
          vehicle2Id,
          startingPointId,
          remarks,
        } = validate({
          ...formValues,
          student,
        });
        let startingPoint: StartingPoint;
        if (startingPointId === fallbackStartingPoint.id) {
          startingPoint = fallbackStartingPoint;
        } else {
          const result = await startingPointsProvider.getOne("ignored", { id: startingPointId });
          startingPoint = result.data;
        }
        const { street, postalCode, city } = startingPoint.postalAddress;
        const edit = {
          eventId: event.id,
          instructorId,
          start: dateTime,
          end: dateTime.plus(duration),
          bufferTimes,
          drivingLessonType,
          bookedTrainingId,
          vehicleId,
          vehicle2Id,
          remarks,
          startLocation: {
            street,
            postalCode,
            city,
          },
        };
        const prices =
          grants.has("viewPrice") &&
          grants.has("changePrice") &&
          drivingSchoolsProvider.getOneFromCache(event.drivingSchoolId)?.customizations.includes("paymentAndInvoicing")
            ? await getLessonPriceDifference(event, edit)
            : undefined;
        return { edit, prices };
      },
      { successMessage: "" },
    );
    if (!pending) return;
    if (pending.prices) {
      setPendingPriceChange({ edit: pending.edit, prices: pending.prices });
      openDialog();
    } else {
      await saveChanges(pending.edit);
    }
  };

  return (
    <Box sx={{ minWidth: { xs: 0, sm: "450px" } }}>
      <FormProvider {...formProps}>
        <Form2Theme>
          <form onSubmit={formProps.handleSubmit(onSubmit)}>
            <Column sx={{ gap: "20px" }}>
              <InstructorInput source="instructorId" size="small" />
              <DateTimeInput
                source="dateTime"
                sx={{ mt: "5px" }}
                allowPastDates
                size="small"
                warning={nightLessonInDaylightWarning}
              />
              <DrivingLessonDurationInput
                source="durationInMinutes"
                options={
                  drivingLessonType === "praktischePruefung"
                    ? PRACTICAL_EXAM_DURATION_OPTIONS
                    : drivingLessonType === "simulator"
                      ? SIMULATOR_DRIVING_LESSON_DURATION_OPTIONS
                      : DRIVING_LESSON_DURATION_OPTIONS
                }
                size="small"
              />
              <BufferTimesInput
                source="bufferTimes"
                preparationTimeLabel="Anfahrt"
                wrapUpTimeLabel="Abfahrt"
                size="small"
              />
              {drivingLessonType !== "praktischePruefung" && (
                <DrivingLessonTypeInput source="drivingLessonType" size="small" />
              )}
              <DrivingLicenseClassInput
                student={student}
                source="drivingLicenseClass"
                label="Ausbildung für Führerscheinklasse"
                showFinishedBookedTrainings
                size="small"
              />
              {drivingLicenseClass && (
                <VehiclesInput
                  instructorId={instructorId}
                  drivingLessonType={drivingLessonType}
                  drivingLicenseClass={drivingLicenseClass}
                  size="small"
                />
              )}
              {student && drivingLessonType !== "simulator" && (
                <StartingPointInput
                  source="startingPointId"
                  drivingLessonType={drivingLessonType}
                  instructorId={instructorId}
                  student={student}
                  size="small"
                  fallbackStartingPoint={startingPointId === fallbackStartingPoint.id && fallbackStartingPoint}
                />
              )}
              <TextInput source="remarks" label="Hinweis für Fahrschüler" multiline rows={2} />
            </Column>
            <Row sx={{ justifyContent: "space-between", mt: "40px" }}>
              <Button label="Zurück" variant="outlined" size="medium" onClick={closeEditMode}>
                <NavigateBeforeIcon />
              </Button>
              <SaveButton2 label="Speichern" />
            </Row>
          </form>
        </Form2Theme>
      </FormProvider>
      {pendingPriceChange && (
        <LessonPriceDifferenceDialog
          {...dialogProps}
          studentName={student.callName}
          isManualPrice={!!event.isManualPrice}
          prices={pendingPriceChange.prices}
          onConfirm={async (choice) => {
            await saveChanges({ ...pendingPriceChange.edit, ...choice });
          }}
        />
      )}
    </Box>
  );
}
